import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { api } from '../lib/api';
import { fieldErrors } from '../lib/form-errors';
import { haptic } from '../lib/telegram';
import type { User } from '../types';
import { Button, Card, ErrorNotice, Field, Input } from './ui';

/**
 * Запасной канал связи. Бот на эту почту ничего не шлёт — она нужна, когда
 * договорить надо, а переписка по заказу уже закрыта: заказ отменён или
 * завершён. Поэтому её видит только вторая сторона заказа и только там.
 */
export function ContactEmailCard({
  role,
  publicToggle,
}: {
  role: 'artist' | 'customer';
  /** Согласие показать почту на публичной странице. Только у художника. */
  publicToggle?: { value: boolean; onChange: (value: boolean) => void; disabled?: boolean };
}) {
  const queryClient = useQueryClient();
  const me = useQuery({ queryKey: ['me'], queryFn: () => api<User>('/me') });
  const [email, setEmail] = useState('');
  const [saved, setSaved] = useState(false);

  // Значение приходит асинхронно, поэтому поле наполняется после загрузки.
  useEffect(() => {
    if (me.data) setEmail(me.data.contactEmail ?? '');
  }, [me.data]);

  const mutation = useMutation({
    mutationFn: () =>
      api<User>('/me', { method: 'PATCH', body: { contactEmail: email.trim() || null } }),
    onSuccess: (updated) => {
      haptic('success');
      queryClient.setQueryData(['me'], updated);
      setSaved(true);
    },
  });
  const errors = fieldErrors(mutation.error);
  const counterpart = role === 'artist' ? 'заказчик' : 'художник';

  return (
    <Card>
      <form
        // Ошибки показывают поля формы: браузерная подсказка дёргала лист.
        noValidate
        className="stack stack--compact"
        onSubmit={(event) => {
          event.preventDefault();
          mutation.mutate();
        }}
      >
        <Field
          label="Почта для связи"
          hint={`Виден ${counterpart}у по заказу, когда переписка в приложении закрыта`}
          error={errors.contactEmail}
        >
          <Input
            type="email"
            inputMode="email"
            value={email}
            placeholder="me@example.com"
            onChange={(event) => {
              setEmail(event.target.value);
              setSaved(false);
            }}
          />
        </Field>
        {/* Публикация — отдельное согласие: почта заводится как запасной контакт
            по заказу, и раскрывать её всем подряд по умолчанию нельзя. */}
        {publicToggle && (
          <>
            <label className="toggle-row">
              <span>Показывать на публичной странице</span>
              <button
                type="button"
                role="switch"
                aria-checked={publicToggle.value}
                aria-label="Показывать почту на публичной странице"
                className={`switch ${publicToggle.value ? 'is-on' : ''}`}
                disabled={publicToggle.disabled}
                onClick={() => publicToggle.onChange(!publicToggle.value)}
              >
                <span />
              </button>
            </label>
            <p className="muted settings-note">
              Включите — почта появится на публичной странице и на картинке профиля.
            </p>
          </>
        )}
        {mutation.error && <ErrorNotice error={mutation.error} />}
        {saved && !mutation.isPending && <div className="notice notice--success">Сохранено</div>}
        <Button
          type="submit"
          disabled={mutation.isPending || email.trim() === (me.data?.contactEmail ?? '')}
        >
          {mutation.isPending ? 'Сохраняем…' : 'Сохранить'}
        </Button>
      </form>
    </Card>
  );
}
